/**
 * @fileoverview Positioning and visible-item rules for home calendar cells.
 * @module components/HomeCalendar/homeCalendarLayout
 */

import {
  getMaxSpanningEventRowForDate,
  getSingleDayEventsForDate,
  getSpanningEventsForDate,
} from "../../logic/homeCalendar";
import {
  BASE_TOP,
  CELL_WIDTH,
  EVENT_HEIGHT,
  MAX_VISIBLE_ITEMS,
  ROW_HEIGHT,
} from "./homeCalendarConstants";

const visibleAppointmentCount = (
  totalItemsCount,
  spanningCount,
  appointmentCount,
) => {
  if (totalItemsCount <= MAX_VISIBLE_ITEMS) {
    return appointmentCount;
  }

  return Math.max(0, MAX_VISIBLE_ITEMS - spanningCount);
};

/**
 * Spanning bars sit above the day number. Their row and height reserve
 * padding so appointment chips do not overlap those bars.
 * @param {number} maxRow
 * @returns {number}
 */
export const getDayTopPadding = (maxRow) => {
  if (maxRow < 0) {
    return 0;
  }

  return BASE_TOP + maxRow * ROW_HEIGHT + EVENT_HEIGHT - BASE_TOP;
};

/**
 * @param {Object} spanningEvent
 * @returns {{left: string, width: string, top: string}}
 */
export const getSpanningEventStyle = (spanningEvent) => {
  const left = spanningEvent.startDayIndex * CELL_WIDTH;
  const spanDays = spanningEvent.endDayIndex - spanningEvent.startDayIndex + 1;
  const width = spanDays * CELL_WIDTH;
  const topOffset = BASE_TOP + spanningEvent.row * ROW_HEIGHT;

  return {
    left: `calc(${left}% + 0.25rem)`,
    width: `calc(${width}% - 0.5rem)`,
    top: `${topOffset}px`,
  };
};

/**
 * Stable key for a spanning bar. A multi-day event can split into more than
 * one segment in the same week, so the segment range is part of the key.
 * @param {Object} spanningEvent
 * @returns {string}
 */
export const spanningEventKey = (spanningEvent) => {
  const identity = spanningEvent.id ?? spanningEvent.name;
  return `spanning-${identity}-${spanningEvent.startDayIndex}-${spanningEvent.endDayIndex}`;
};

/**
 * @param {Object} event
 * @returns {string|number}
 */
export const eventIndicatorKey = (event) => {
  const identity = event.id ?? `${event.name}-${event.time}`;
  return `${event.type}-${identity}`;
};

/**
 * @param {Object} event
 * @returns {string}
 */
export const eventIndicatorTitle = (event) => {
  const timePrefix = event.time ? `${event.time} - ` : "";
  return `${timePrefix}${event.name}`;
};

/**
 * @param {Array<Object>} week
 * @returns {number|string}
 */
export const weekKey = (week) => week[0].date.getTime();

/**
 * @param {Object} day
 * @returns {number}
 */
export const dayKey = (day) => day.date.getTime();

/**
 * Day-cell contents. Spanning events render as bars, and they still count
 * toward the visible-item limit. Past that limit the cell shows a "more" count.
 * @param {Object} day
 * @param {Array<Object>} week
 * @param {Object} calendarData
 * @param {Function} t
 * @param {string} language
 * @returns {{itemsToShow: Array, remainingCount: number, topPadding: number}}
 */
export const getDayCellView = (day, week, calendarData, t, language) => {
  const spanningEventsForDay = getSpanningEventsForDate(day.date, calendarData);
  const appointmentsForDay = getSingleDayEventsForDate(
    day.date,
    calendarData,
    t,
    language,
  );
  const totalItemsCount =
    spanningEventsForDay.length + appointmentsForDay.length;
  const maxRow = getMaxSpanningEventRowForDate(day.date, week, calendarData);
  const appointmentsToShow = visibleAppointmentCount(
    totalItemsCount,
    spanningEventsForDay.length,
    appointmentsForDay.length,
  );
  const itemsToShow = appointmentsForDay.slice(0, appointmentsToShow);

  return {
    itemsToShow,
    remainingCount:
      totalItemsCount - (spanningEventsForDay.length + appointmentsToShow),
    topPadding: getDayTopPadding(maxRow),
  };
};
